import React, { useEffect, useState } from "react";
import { LibraryFolderNode } from "../../API/Library/LibraryCommands";
import { ChevronIcon, FolderIcon } from "./LibraryIcons";
import FolderMenu from "./FolderMenu";
import { ancestorIds } from "./libraryUtils";
import styles from "./Library.module.css";

type FolderTreeProps = {
  folders: LibraryFolderNode[];
  activeId: string;
  dropOver: string;
  canRename: boolean;
  loading?: boolean;
  onOpen: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onMoveFolder: (id: string, title: string) => void;
  onDeleteFolder: (id: string) => void;
  onDropOver: (id: string) => void;
  onDropFiles: (files: FileList | null, folderId: string) => void;
  onMove: (folderId: string, draggedFileId?: string) => void;
};

const expandableFolderIds = (nodes: LibraryFolderNode[]): string[] => {
  const ids: string[] = [];
  const walk = (list: LibraryFolderNode[]) => {
    for (const folder of list) {
      const children = folder.folders ?? [];
      if (children.length > 0) {
        ids.push(folder.id);
        walk(children);
      }
    }
  };
  walk(nodes);
  return ids;
};

const FolderTree = ({
  folders,
  activeId,
  dropOver,
  canRename,
  loading,
  onOpen,
  onRename,
  onMoveFolder,
  onDeleteFolder,
  onDropOver,
  onDropFiles,
  onMove,
}: FolderTreeProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!activeId) return;
    const parents = ancestorIds(folders, activeId) ?? [];
    setExpanded((current) => {
      const next = new Set(current);
      parents.forEach((id) => next.add(id));
      next.add(activeId);
      return next;
    });
  }, [activeId, folders]);

  const toggle = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandable = expandableFolderIds(folders);
  const allExpanded = expandable.length > 0 && expandable.every((id) => expanded.has(id));

  return (
    <aside className={styles.tree}>
      <div className={styles.treeLabelBar}>
        <span>Folders</span>
        {expandable.length > 0 && (
          <button
            type="button"
            className={styles.treeExpandAll}
            onClick={() => setExpanded(allExpanded ? new Set() : new Set(expandable))}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        )}
      </div>
      {loading && <TreeSkeleton />}
      {!loading && folders.length === 0 && (
        <p className={styles.treeEmpty}>No folders yet</p>
      )}
      {!loading && folders.map((folder) => (
        <FolderBranch
          key={folder.id}
          folder={folder}
          depth={0}
          activeId={activeId}
          dropOver={dropOver}
          expanded={expanded}
          canRename={canRename}
          onOpen={onOpen}
          onToggle={toggle}
          onRename={onRename}
          onMoveFolder={onMoveFolder}
          onDeleteFolder={onDeleteFolder}
          onDropOver={onDropOver}
          onDropFiles={onDropFiles}
          onMove={onMove}
        />
      ))}
    </aside>
  );
};

const FolderBranch = ({
  folder,
  depth,
  activeId,
  dropOver,
  expanded,
  canRename,
  onOpen,
  onToggle,
  onRename,
  onMoveFolder,
  onDeleteFolder,
  onDropOver,
  onDropFiles,
  onMove,
}: {
  folder: LibraryFolderNode;
  depth: number;
  activeId: string;
  dropOver: string;
  expanded: Set<string>;
  canRename: boolean;
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onMoveFolder: (id: string, title: string) => void;
  onDeleteFolder: (id: string) => void;
  onDropOver: (id: string) => void;
  onDropFiles: (files: FileList | null, folderId: string) => void;
  onMove: (folderId: string, draggedFileId?: string) => void;
}) => {
  const hasChildren = (folder.folders ?? []).length > 0;
  const isOpen = hasChildren && expanded.has(folder.id);
  return (
    <div className={depth > 0 ? styles.treeBranch : undefined}>
      <div
        className={`${styles.treeRow} ${activeId === folder.id ? styles.active : ""} ${dropOver === folder.id ? styles.dropTarget : ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          onDropOver(folder.id);
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.dataTransfer.files?.length) onDropFiles(event.dataTransfer.files, folder.id);
          else onMove(folder.id, event.dataTransfer.getData("text/file-id"));
        }}
      >
        <div className={styles.treeItem} style={{ paddingLeft: 8 + depth * 14 }}>
          <button
            type="button"
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
            aria-label={hasChildren ? (isOpen ? `Collapse ${folder.title}` : `Expand ${folder.title}`) : undefined}
            disabled={!hasChildren}
            onClick={() => {
              if (hasChildren) onToggle(folder.id);
            }}
          >
            {hasChildren ? <ChevronIcon size={12} /> : <span style={{ width: 12 }} />}
          </button>
          <button type="button" className={styles.treeOpen} onClick={() => onOpen(folder.id)}>
            <span className={styles.folderGlyph}><FolderIcon size={16} /></span>
            <span className={styles.treeLabel}>{folder.title}</span>
          </button>
        </div>
        {canRename && (
          <div className={styles.treeRename}>
            <FolderMenu
              folderId={folder.id}
              title={folder.title}
              position="right"
              onRename={onRename}
              onMove={onMoveFolder}
              onDelete={onDeleteFolder}
            />
          </div>
        )}
      </div>
      {isOpen && (folder.folders ?? []).map((child) => (
        <FolderBranch
          key={child.id}
          folder={child}
          depth={depth + 1}
          activeId={activeId}
          dropOver={dropOver}
          expanded={expanded}
          canRename={canRename}
          onOpen={onOpen}
          onToggle={onToggle}
          onRename={onRename}
          onMoveFolder={onMoveFolder}
          onDeleteFolder={onDeleteFolder}
          onDropOver={onDropOver}
          onDropFiles={onDropFiles}
          onMove={onMove}
        />
      ))}
    </div>
  );
};

const TreeSkeleton = () => (
  <div className={styles.skeletonStack}>
    {[72, 58, 64, 48, 68, 52].map((width, index) => (
      <div key={index} className={styles.skeletonLine} style={{ width: `${width}%`, marginLeft: index % 3 === 2 ? "1rem" : 0 }} />
    ))}
  </div>
);

export default FolderTree;
